import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import {
  DataAccessEventsFacade,
  DataAccessExhibitorsFacade,
  DataAccessPostsFacade,
  DataAccessProductsFacade,
} from '@planone/bau365/data-access-fair-data';
import { Event, Exhibitor, Post, Product } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { filter, map, of, switchMap, take, withLatestFrom } from 'rxjs';

import * as TabChangeActions from './tab-change.actions';
import {
  EventTabClickEventInterface,
  ExhibitorTabClickEventInterface,
  PostTabClickEventInterface,
  ProductTabClickEventInterface,
} from './tab-change.models';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class TabChangeEffects {
  generalTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TabChangeActions.tabChange),
      filter(
        (action: any) =>
          action.entityType === undefined || action.entityType === 'GENERAL' || action.entityType === 'UNKNOWN'
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) =>
        of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.tab_click,
              data: {
                tab_label: action.tabLabel,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'tab_click',
            tab_label: action.tabLabel,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
          })
        )
      )
    )
  );

  eventTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TabChangeActions.tabChange),
      filter((action: any) => action.entityType === 'EVENT'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event),
          take(1),
          map((event) => [action, event])
        );
      }),
      switchMap(([action, event]) =>
        this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) =>
              <[EventTabClickEventInterface, Event, Exhibitor, boolean]>[action, event, exhibitor, exclude_analytics]
          ),
          switchMap(([action, event, exhibitor, exclude_analytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowEventContext = this.dataTransformationHelper.snowplow?.map('EVENT', event);

            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowEventContext) snowplowContext.push(snowplowEventContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.tab_click,
                  data: {
                    tab_label: action.tabLabel,
                    context: action.context,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'tab_click',
                tab_label: action.tabLabel,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
              })
            );
          })
        )
      )
    )
  );

  exhibitorTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TabChangeActions.tabChange),
      filter((action: any) => action.entityType === 'EXHIBITOR'),
      switchMap((action) => {
        return this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          map((exhibitor) => [action, exhibitor])
        );
      }),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      map(
        ([[action, exhibitor], exclude_analytics]) =>
          <[ExhibitorTabClickEventInterface, Exhibitor, boolean]>[action, exhibitor, exclude_analytics]
      ),
      switchMap(([action, exhibitor, exclude_analytics]) => {
        const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);

        return of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.tab_click,
              data: {
                tab_label: action.tabLabel,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
            context: snowplowExhibitorContext ? [snowplowExhibitorContext] : undefined,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'tab_click',
            tab_label: action.tabLabel,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
          })
        );
      })
    )
  );

  postTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TabChangeActions.tabChange),
      filter((action: any) => action.entityType === 'POST'),
      switchMap((action) => {
        return this.postsFacade.post$.pipe(
          filter((post) => !!post),
          take(1),
          map((post) => [action, post])
        );
      }),
      switchMap(([action, post]) =>
        this.exhibitorsFacade.getExhibitorById(post?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) =>
              <[PostTabClickEventInterface, Post, Exhibitor, boolean]>[action, post, exhibitor, exclude_analytics]
          ),
          switchMap(([action, post, exhibitor, exclude_analytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowPostContext = this.dataTransformationHelper.snowplow?.map('POST', post);

            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowPostContext) snowplowContext.push(snowplowPostContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.tab_click,
                  data: {
                    tab_label: action.tabLabel,
                    context: action.context,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'tab_click',
                tab_label: action.tabLabel,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('POST', post),
              })
            );
          })
        )
      )
    )
  );

  productTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TabChangeActions.tabChange),
      filter((action: any) => action.entityType === 'PRODUCT'),
      switchMap((action) => {
        return this.productsFacade.product$.pipe(
          filter((product) => !!product),
          take(1),
          map((product) => [action, product])
        );
      }),
      switchMap(([action, product]) =>
        this.exhibitorsFacade.getExhibitorById(product?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) =>
              <[ProductTabClickEventInterface, Product, Exhibitor, boolean]>[
                action,
                product,
                exhibitor,
                exclude_analytics,
              ]
          ),
          switchMap(([action, product, exhibitor, exclude_analytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowProductContext = this.dataTransformationHelper.snowplow?.map(
              'PRODUCT',
              product,
              exhibitor.fair_id
            );

            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowProductContext) snowplowContext.push(snowplowProductContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.tab_click,
                  data: {
                    tab_label: action.tabLabel,
                    context: action.context,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'tab_click',
                tab_label: action.tabLabel,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('PRODUCT', product),
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    private eventsFacade: DataAccessEventsFacade,
    private postsFacade: DataAccessPostsFacade,
    private productsFacade: DataAccessProductsFacade,
    private dataTransformationHelper: DataTransformationHelper
  ) {}
}
